import { createFileRoute } from "@tanstack/react-router";
import type { ClientLocationBaseData } from "api-types/locations/locationsResponses";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import BuilderPortalLayout from "lib/components/builders/BuilderPortalLayout";
import styles from "lib/components/builders/communities/lots/LotDetails.module.scss";
import LotDetailsForm from "lib/components/builders/communities/lots/LotDetailsForm";
import getLotNumberDisplay from "lib/components/builders/communities/plans/getLotNumberDisplay";
import { shouldDisplayPricing } from "lib/components/builders/communities/utils/pricingVisibility";
import { useLocationHierarchy } from "lib/components/builders/communities/utils/useLocationHierarchy";
import { getBuilderLocationBaseDataURL, getBuilderLotURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { locationHomePlansPath, locationLotsPath, useAppRouterParams } from "lib/routeHelpers";
import type { LotRowData } from "lib/state/LotsContext";
import { LotDetailsContext } from "lib/state/LotsContext";
import type { ReactNode } from "react";
import Loading from "ui/components/Loading";
import withAuthentication from "../../../../../../../components/withAuthentication";

function LotPage() {
  const { builderId, lotId, locationId } = useAppRouterParams();

  if (!builderId || !lotId || !locationId) {
    return null;
  }

  return <ReadyLotPage builderId={builderId} lotId={lotId} locationId={locationId} />;
}

interface ReadyLotPageProps {
  builderId: string;
  lotId: string;
  locationId: string;
}

function ReadyLotPage({ builderId, lotId, locationId }: ReadyLotPageProps): ReactNode {
  const { data: locationHierarchy } = useLocationHierarchy(builderId, locationId);

  const { loading: locationDataLoading, data: locationData } = useFetch<ClientLocationBaseData>(
    getBuilderLocationBaseDataURL({ builderId, locationId }),
    [builderId, locationId],
  );
  const { loading: lotRowDataLoading, data: lot } = useFetch<LotRowData>(
    getBuilderLotURL({ builderId, locationId, lotId }),
    [builderId, locationId, lotId],
  );

  if (locationDataLoading || lotRowDataLoading) {
    return <Loading />;
  }

  assertIsNotUndefined(locationData);
  assertIsNotUndefined(lot);

  const locationName = locationData.location.name;
  const displayPrices = shouldDisplayPricing(
    "communityPlans",
    locationData.location,
    locationHierarchy?.ancestors,
    locationData.builder,
  );

  const breadcrumbs = [
    {
      text: locationName,
      href: locationHomePlansPath(builderId, locationId),
    },
    {
      text: "Lots",
      href: locationLotsPath(builderId, locationId),
    },
  ];

  return (
    <LotDetailsContext.Provider
      value={{
        builderId,
        community: locationData.location,
        displayPrices,
        lot,
      }}
    >
      <BuilderPortalLayout
        selected="locations"
        pageTitle={locationName}
        header={`Edit Lot ${getLotNumberDisplay(lot)}`}
        breadcrumbs={breadcrumbs}
      >
        <div className="builder-portal-layout-content">
          <main className={styles.lotDetailsContainer}>
            <LotDetailsForm />
          </main>
        </div>
      </BuilderPortalLayout>
    </LotDetailsContext.Provider>
  );
}

export const Route = createFileRoute("/builders/$builder_id/locations/$location_id/lots/$lot_id/")({
  component: withAuthentication(LotPage, { permissions: ["VIEW_LOTS"] }),
});
